import { t } from '@/i18n/translations';
import { BrandLogo } from '@/components/layout/BrandLogo';

/** Low-opacity HPDC die / cavity blueprint — background only, never illustration. */
function DieCastBlueprint() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full text-sidebar-foreground opacity-[0.11]"
      viewBox="0 0 800 900"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <pattern id="hpdc-grid" width="80" height="80" patternUnits="userSpaceOnUse">
          <path d="M80 0H0V80" stroke="currentColor" strokeWidth="0.4" />
        </pattern>
      </defs>
      <rect width="800" height="900" fill="url(#hpdc-grid)" opacity="0.4" />

      {/* Die plate */}
      <rect x="168" y="196" width="464" height="508" stroke="currentColor" strokeWidth="1.05" />
      <rect x="184" y="212" width="432" height="476" stroke="currentColor" strokeWidth="0.55" />

      {/* Parting line */}
      <path d="M132 450H668" stroke="currentColor" strokeWidth="0.65" strokeDasharray="7 5" />

      {/* Sprue well + runner + gates */}
      <circle cx="400" cy="230" r="13" stroke="currentColor" strokeWidth="0.85" />
      <circle cx="400" cy="230" r="4.5" stroke="currentColor" strokeWidth="0.65" />
      <path d="M400 243 V314" stroke="currentColor" strokeWidth="2" />
      <path d="M318 314 H482" stroke="currentColor" strokeWidth="1.85" />
      <path d="M338 314 V346" stroke="currentColor" strokeWidth="1.25" />
      <path d="M400 314 V346" stroke="currentColor" strokeWidth="1.25" />
      <path d="M462 314 V346" stroke="currentColor" strokeWidth="1.25" />

      {/* Cavity / cast housing */}
      <path
        d="M252 348 H428 C450 348 454 356 454 372 V408 H528 V558 H454 V588 H252 V528 H214 V408 H252 Z"
        stroke="currentColor"
        strokeWidth="1.1"
      />
      <path d="M280 390 H404 V526 H280 Z" stroke="currentColor" strokeWidth="0.65" />

      {/* Cored holes + boss */}
      <circle cx="294" cy="372" r="6.5" stroke="currentColor" strokeWidth="0.75" />
      <circle cx="416" cy="372" r="6.5" stroke="currentColor" strokeWidth="0.75" />
      <circle cx="294" cy="570" r="6.5" stroke="currentColor" strokeWidth="0.75" />
      <circle cx="416" cy="570" r="6.5" stroke="currentColor" strokeWidth="0.75" />
      <circle cx="498" cy="484" r="9.5" stroke="currentColor" strokeWidth="0.75" />
      <circle cx="498" cy="484" r="3.2" stroke="currentColor" strokeWidth="0.55" />

      {/* Overflow / vent */}
      <path d="M528 468 H558 V502 H528" stroke="currentColor" strokeWidth="0.7" />

      {/* Section marks */}
      <path d="M214 348 V588" stroke="currentColor" strokeWidth="0.45" strokeDasharray="2.5 3" />

      {/* Dimension ticks */}
      <path d="M252 638 H528" stroke="currentColor" strokeWidth="0.5" />
      <path d="M252 630 V646" stroke="currentColor" strokeWidth="0.5" />
      <path d="M528 630 V646" stroke="currentColor" strokeWidth="0.5" />
      <path d="M128 348 V588" stroke="currentColor" strokeWidth="0.5" />
      <path d="M120 348 H136" stroke="currentColor" strokeWidth="0.5" />
      <path d="M120 588 H136" stroke="currentColor" strokeWidth="0.5" />
    </svg>
  );
}

export function AuthShell({ lang, title, description, children }) {
  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[minmax(0,44fr)_minmax(0,56fr)]">
      <aside className="relative hidden overflow-hidden bg-sidebar text-sidebar-foreground lg:flex">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_18%_8%,hsl(220_22%_22%/0.32),transparent_54%),radial-gradient(ellipse_at_88%_90%,hsl(222_24%_16%/0.2),transparent_48%)]" />
        <DieCastBlueprint />
        <div className="relative z-10 flex w-full flex-col justify-between px-12 py-12 xl:px-16">
          <div className="flex items-center gap-3">
            <BrandLogo onDark imgClassName="h-8" />
          </div>
          <div className="max-w-md space-y-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/50">
              {t('login_brand_kicker', lang)}
            </p>
            <h1 className="text-[2.35rem] font-semibold leading-[1.12] tracking-[-0.03em]">
              HPDC RFQ
            </h1>
            <p className="text-[0.975rem] leading-relaxed text-sidebar-foreground/70">
              {t('login_brand_lead', lang)}
            </p>
            <ul className="space-y-2.5 pt-1 text-sm text-sidebar-foreground/62">
              {['login_brand_point_1', 'login_brand_point_2', 'login_brand_point_3'].map((key) => (
                <li key={key} className="flex gap-3">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                  <span>{t(key, lang)}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-sidebar-foreground/38">{t('login_subtitle', lang)}</p>
        </div>
      </aside>

      <div className="flex items-center justify-center px-6 py-10 sm:px-10 lg:px-14">
        <div className="w-full max-w-[456px]">
          <div className="mb-8 flex flex-col items-start gap-5 lg:hidden">
            <BrandLogo imgClassName="h-10" />
          </div>
          {title ? (
            <div className="mb-8 space-y-1.5">
              <h2 className="page-title">{title}</h2>
              {description ? (
                <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
              ) : null}
            </div>
          ) : null}
          {children}
        </div>
      </div>
    </div>
  );
}
